/**
 * Forgiving free-form time parser. Returns a normalized 24h "HH:MM" if the input
 * can be understood, null otherwise. Accepts the natural shapes people type:
 *
 *   "9"          → "09:00"   (bare hour)
 *   "18"         → "18:00"
 *   "9:3"        → "09:30"   (partial minutes)
 *   "9.30"       → "09:30"   (dot separator)
 *   "930"        → "09:30"   (compact digits)
 *   "1830"       → "18:30"
 *   "900"        → "09:00"
 *   "9p" / "9 pm"/ "9pm"     → "21:00"
 *   "430p"       → "16:30"
 *   "9:30 PM"    → "21:30"   (canonical forms)
 *   "13:05"      → "13:05"
 */
export function parseTimeInput(input: string): string | null {
  const trimmed = input.trim().toLowerCase()
  if (!trimmed) return null

  // Pull off a trailing meridiem ("am"/"pm"/"a"/"p"), tolerating an optional
  // space or dot before it ("9 pm", "9.pm", "430p").
  const meridiemMatch = trimmed.match(/[.\s]*(am|pm|a|p)$/)
  let meridiem: 'am' | 'pm' | null = null
  let core = trimmed
  if (meridiemMatch) {
    meridiem = meridiemMatch[1].startsWith('p') ? 'pm' : 'am'
    core = trimmed.slice(0, meridiemMatch.index).trim()
  }

  let hours: number
  let minutes: number

  const separated = core.match(/^(\d{1,2})[:.](\d{1,2})$/)
  if (separated) {
    // "H:MM" / "H.MM" / "H:M" — minutes may be a single digit ("9:3" → 9:30).
    hours = parseInt(separated[1], 10)
    minutes = parseInt(separated[2].padEnd(2, '0'), 10)
  } else if (/^\d+$/.test(core)) {
    // Compact, separator-free digits.
    const digits = core
    if (digits.length <= 2) {
      // Bare hour: "9", "18".
      hours = parseInt(digits, 10)
      minutes = 0
    } else if (digits.length === 3) {
      // "930" → 9:30, "900" → 9:00.
      hours = parseInt(digits.slice(0, 1), 10)
      minutes = parseInt(digits.slice(1), 10)
    } else if (digits.length === 4) {
      // "1830" → 18:30.
      hours = parseInt(digits.slice(0, 2), 10)
      minutes = parseInt(digits.slice(2), 10)
    } else {
      return null
    }
  } else {
    return null
  }

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
  if (minutes > 59) return null

  if (meridiem) {
    if (hours < 1 || hours > 12) return null
    if (meridiem === 'pm' && hours < 12) hours += 12
    if (meridiem === 'am' && hours === 12) hours = 0
  } else if (hours > 23) {
    return null
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}
